import type { FC, ReactNode } from 'react'
import type { Device, Rack } from '@/types'

import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { useDatacenterStore } from '@/components/datacenter/store'
import { useInventory } from '@/components/datacenter/inventory-context'
import { Meter, StatusDot, glass } from '@/components/datacenter/hud/primitives'
import CmsText from '@/components/datacenter/cms-text'

import {
    AlertTriangle,
    ArrowLeft,
    Check,
    Copy,
    Gauge,
    Layers,
    Search,
    Thermometer,
    X,
    Zap
} from 'lucide-react'

import {
    deviceVisual,
    formatInteger,
    summarizeRack,
    thermalColor,
    toCategory,
    toStatus,
    unitRange
} from '@/lib/inventory'

import { CATEGORY_META, STATUS_META, STATUS_ORDER } from '@/constants/inventory'

const ease = [0.16, 1, 0.3, 1] as const
const UNIT_PX = 12

const dateFormat = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: 'UTC' })

const formatDate = (value: string | null): string | null => {
    if (!value) return null

    const date = new Date(`${value.slice(0, 10)}T00:00:00Z`)

    return Number.isNaN(date.getTime()) ? null : dateFormat.format(date)
}

const contentMotion = {
    initial: { opacity: 0, y: 14, filter: 'blur(6px)' },
    animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
    exit: { opacity: 0, y: -10, filter: 'blur(6px)' },
    transition: { duration: 0.35, ease }
}

interface RackElevationProps {
    rack: Rack
    devices: Device[]
}

const RackElevation: FC<RackElevationProps> = ({ rack, devices }): ReactNode => {
    const setHoveredDevice = useDatacenterStore(state => state.setHoveredDevice)
    const selectDevice = useDatacenterStore(state => state.selectDevice)
    const hoveredId = useDatacenterStore(state => state.hoveredDeviceId)
    const units = rack.units
    const labels = Array.from({ length: Math.floor(units / 6) + 1 }, (_, index) => units - index * 6).filter(u => u >= 1)

    return (
        <div className='relative mt-3 flex gap-2'>
            <div className='relative w-6 shrink-0 font-mono text-[9px] text-slate-500' style={{ height: units * UNIT_PX }} aria-hidden='true'>
                {labels.map(u => (
                    <span key={u} className='absolute right-0' style={{ top: (units - u) * UNIT_PX }}>
                        {u}
                    </span>
                ))}
            </div>

            <div
                className='relative flex-1 overflow-hidden rounded-lg border border-white/10 bg-[repeating-linear-gradient(180deg,rgba(255,255,255,0.035)_0px,rgba(255,255,255,0.035)_11px,transparent_11px,transparent_12px)]'
                style={{ height: units * UNIT_PX }}
            >
                <ul aria-label='Rack elevation'>
                    {devices.map((device, index) => {
                        const height = Math.min(device.u_height, units)
                        const start = Math.min(Math.max(1, device.u_position), units - height + 1)
                        const top = (units - (start + height - 1)) * UNIT_PX
                        const category = toCategory(device.category)
                        const status = toStatus(device.status)
                        const color = CATEGORY_META[category].color
                        const hovered = hoveredId === device.id

                        return (
                            <motion.li
                                key={device.id}
                                className='absolute inset-x-0'
                                style={{ top, height: height * UNIT_PX - 1, originX: 0 }}
                                initial={{ scaleX: 0, opacity: 0 }}
                                animate={{ scaleX: 1, opacity: 1 }}
                                transition={{ delay: 0.1 + index * 0.018, duration: 0.45, ease }}
                            >
                                <button
                                    type='button'
                                    onMouseEnter={() => setHoveredDevice(device.id)}
                                    onMouseLeave={() => setHoveredDevice(null)}
                                    onFocus={() => setHoveredDevice(device.id)}
                                    onBlur={() => setHoveredDevice(null)}
                                    onClick={() => selectDevice(device.id, rack.id)}
                                    className={cn('flex size-full items-center gap-1.5 overflow-hidden rounded-[3px] border-l-2 px-1.5 text-left transition focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white', hovered && 'brightness-150')}
                                    style={{
                                        borderLeftColor: STATUS_META[status].color,
                                        background: `linear-gradient(90deg, ${color}38, ${color}14)`
                                    }}
                                    aria-label={`${device.hostname}, ${unitRange(device)}`}
                                >
                                    <CmsText table='devices' id={device.id} field='hostname' value={device.hostname} className='truncate font-mono text-[9.5px] leading-none text-slate-100' />
                                </button>
                            </motion.li>
                        )
                    })}
                </ul>
            </div>
        </div>
    )
}

interface RackDetailProps {
    rack: Rack
    devices: Device[]
}

const RackDetail: FC<RackDetailProps> = ({ rack, devices }): ReactNode => {
    const selectRack = useDatacenterStore(state => state.selectRack)
    const summary = useMemo(() => summarizeRack(rack, devices), [rack, devices])
    const zone = rack.zone.toLowerCase().startsWith('comp') ? 'server' : toCategory(rack.zone)
    const zoneMeta = CATEGORY_META[zone]
    const capacity = rack.power_capacity_kw ?? 0

    return (
        <motion.div {...contentMotion} className='flex min-h-0 flex-1 flex-col'>
            <div className='border-b border-white/10 p-5'>
                <div className='flex items-center justify-between gap-3'>
                    <span className='inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium' style={{ backgroundColor: `${zoneMeta.color}1a`, color: zoneMeta.color }}>
                        <span className='size-1.5 rounded-full' style={{ backgroundColor: zoneMeta.color }} />
                        <CmsText table='racks' id={rack.id} field='zone' value={rack.zone} className='capitalize' />
                    </span>
                    <button
                        type='button'
                        onClick={() => selectRack(null)}
                        className='flex size-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70'
                        aria-label='Close rack details'
                    >
                        <X className='size-4' aria-hidden='true' />
                    </button>
                </div>

                <h2 className='mt-3 font-mono text-3xl font-semibold tracking-tight text-white'>
                    <CmsText table='racks' id={rack.id} field='code' value={rack.code} />
                </h2>
                {rack.name && <CmsText as='p' table='racks' id={rack.id} field='name' value={rack.name} className='mt-1 block text-sm text-slate-300' />}
                <p className='mt-1 text-xs text-slate-500'>
                    <span>
                        Row
                    </span>{' '}
                    <CmsText table='racks' id={rack.id} field='row_label' value={rack.row_label} className='text-slate-300' />
                    <span aria-hidden='true'> · </span>
                    <span>Position </span>
                    <span>{rack.position}</span>
                    <span aria-hidden='true'> · </span>
                    <span>{rack.units}</span>
                    <span>U</span>
                </p>

                <div className='mt-4 grid grid-cols-3 gap-2'>
                    <div className='rounded-xl border border-white/10 bg-white/[0.03] p-3'>
                        <p className='flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-slate-500'>
                            <Zap className='size-3' aria-hidden='true' />
                            Power
                        </p>
                        <p className='mt-1 font-heading text-base font-semibold text-white'>
                            {summary.powerKw.toFixed(1)}
                            <span className='ml-0.5 text-xs font-normal text-slate-400'>
                                kW
                            </span>
                        </p>
                        {capacity > 0 && <Meter value={summary.powerKw} max={capacity} color={summary.powerKw / capacity > 0.85 ? '#fb923c' : '#22d3ee'} className='mt-2' />}
                    </div>
                    <div className='rounded-xl border border-white/10 bg-white/[0.03] p-3'>
                        <p className='flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-slate-500'>
                            <Layers className='size-3' aria-hidden='true' />
                            Space
                        </p>
                        <p className='mt-1 font-heading text-base font-semibold text-white'>
                            {summary.usedUnits}
                            <span className='ml-0.5 text-xs font-normal text-slate-400'>
                                <span> / </span>
                                <span>{rack.units}</span>
                                <span>U</span>
                            </span>
                        </p>
                        <Meter value={summary.usedUnits} max={rack.units} color='#a78bfa' className='mt-2' />
                    </div>
                    <div className='rounded-xl border border-white/10 bg-white/[0.03] p-3'>
                        <p className='flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-slate-500'>
                            <Thermometer className='size-3' aria-hidden='true' />
                            Temp
                        </p>
                        <p className='mt-1 font-heading text-base font-semibold' style={{ color: thermalColor(summary.avgTemp) }}>
                            {summary.avgTemp !== null ? summary.avgTemp.toFixed(1) : '-'}
                            <span className='ml-0.5 text-xs font-normal text-slate-400'>
                                °C
                            </span>
                        </p>
                        <p className='mt-1.5 text-[10px] text-slate-500'>
                            Peak {summary.maxTemp !== null ? `${summary.maxTemp.toFixed(1)} °C` : '-'}
                        </p>
                    </div>
                </div>

                <ul className='mt-3 flex flex-wrap gap-1.5'>
                    {STATUS_ORDER.filter(status => summary.byStatus[status] > 0).map(status => (
                        <li key={status} className='inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[11px] text-slate-300'>
                            <StatusDot status={status} />
                            {summary.byStatus[status]} {STATUS_META[status].label.toLowerCase()}
                        </li>
                    ))}
                </ul>
            </div>

            <div className='min-h-0 flex-1 overflow-y-auto p-5 pt-4'>
                <p className='flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500'>
                    <span>
                        Rack elevation
                    </span>
                    <span className='normal-case tracking-normal text-slate-500'>
                        Click a device to pull it out
                    </span>
                </p>
                <RackElevation rack={rack} devices={devices} />
            </div>
        </motion.div>
    )
}

interface SpecProps {
    label: string
    children: ReactNode
}

const Spec: FC<SpecProps> = ({ label, children }): ReactNode => {
    return (
        <div className='flex items-baseline justify-between gap-4 border-b border-white/[0.06] py-2 last:border-0'>
            <dt className='shrink-0 text-xs text-slate-500'>
                {label}
            </dt>
            <dd className='min-w-0 text-right text-sm text-slate-100'>
                {children}
            </dd>
        </div>
    )
}

interface DeviceDetailProps {
    device: Device
    rack: Rack | undefined
}

const DeviceDetail: FC<DeviceDetailProps> = ({ device, rack }): ReactNode => {
    const back = useDatacenterStore(state => state.back)
    const selectRack = useDatacenterStore(state => state.selectRack)
    const setSearch = useDatacenterStore(state => state.setSearch)
    const setInventoryOpen = useDatacenterStore(state => state.setInventoryOpen)
    const setCategoryFilter = useDatacenterStore(state => state.setCategoryFilter)
    const setStatusFilter = useDatacenterStore(state => state.setStatusFilter)
    const [copied, setCopied] = useState(false)
    const [today, setToday] = useState<string | null>(null)
    const status = toStatus(device.status)
    const category = toCategory(device.category)
    const categoryMeta = CATEGORY_META[category]
    const installed = formatDate(device.installed_on)
    const warranty = formatDate(device.warranty_until)
    const expired = Boolean(today && device.warranty_until && device.warranty_until.slice(0, 10) < today)

    useEffect(() => {
        setToday(new Date().toISOString().slice(0, 10))
    }, [])

    const copyHostname = async () => {
        try {
            await navigator.clipboard.writeText(device.hostname)
            setCopied(true)
            window.setTimeout(() => setCopied(false), 1600)
        }
        catch {
            setCopied(false)
        }
    }

    const findInInventory = () => {
        setCategoryFilter('all')
        setStatusFilter('all')
        setSearch(device.hostname)
        setInventoryOpen(true)
    }

    return (
        <motion.div {...contentMotion} className='flex min-h-0 flex-1 flex-col'>
            <div className='border-b border-white/10 p-5'>
                <div className='flex items-center justify-between gap-3'>
                    {rack ? (
                        <button
                            type='button'
                            onClick={back}
                            className='inline-flex items-center gap-1.5 rounded-lg py-1 pr-2 text-xs text-slate-400 transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70'
                        >
                            <ArrowLeft className='size-3.5' aria-hidden='true' />
                            <span>
                                Back to rack
                            </span>
                            <CmsText table='racks' id={rack.id} field='code' value={rack.code} className='font-mono text-slate-200' />
                        </button>
                    ) : (
                        <span className='text-xs text-slate-500'>
                            Not placed in a rack
                        </span>
                    )}
                    <button
                        type='button'
                        onClick={() => selectRack(null)}
                        className='flex size-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70'
                        aria-label='Close device details'
                    >
                        <X className='size-4' aria-hidden='true' />
                    </button>
                </div>

                <div className='mt-3 flex flex-wrap items-center gap-2'>
                    <span className='inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-xs font-medium text-slate-200'>
                        <StatusDot status={status} />
                        <CmsText table='devices' id={device.id} field='status' value={device.status} className='capitalize' />
                    </span>
                    <span className='inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium' style={{ backgroundColor: `${categoryMeta.color}1a`, color: categoryMeta.color }}>
                        <CmsText table='devices' id={device.id} field='category' value={device.category} className='capitalize' />
                    </span>
                </div>

                <h2 className='mt-3 break-all font-mono text-xl font-semibold leading-tight text-white'>
                    <CmsText table='devices' id={device.id} field='hostname' value={device.hostname} />
                </h2>
                <p className='mt-1 text-sm text-slate-300'>
                    <CmsText table='devices' id={device.id} field='type' value={device.type} />
                    {device.role && (
                        <>
                            <span className='text-slate-600' aria-hidden='true'> · </span>
                            <CmsText table='devices' id={device.id} field='role' value={device.role} className='text-slate-400' />
                        </>
                    )}
                </p>

                {device.notes && (
                    <div
                        className='mt-4 flex gap-2.5 rounded-xl border p-3 text-sm'
                        style={{ borderColor: `${STATUS_META[status].color}40`, backgroundColor: `${STATUS_META[status].color}12` }}
                    >
                        <AlertTriangle className='mt-0.5 size-4 shrink-0' style={{ color: STATUS_META[status].color }} aria-hidden='true' />
                        <CmsText as='p' table='devices' id={device.id} field='notes' value={device.notes} className='leading-relaxed text-slate-200' />
                    </div>
                )}

                <div className='mt-4 grid grid-cols-2 gap-2'>
                    <div className='rounded-xl border border-white/10 bg-white/[0.03] p-3'>
                        <p className='flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-slate-500'>
                            <Gauge className='size-3' aria-hidden='true' />
                            Power draw
                        </p>
                        <p className='mt-1 font-heading text-lg font-semibold text-white'>
                            {device.power_w !== null ? formatInteger(device.power_w) : '-'}
                            <span className='ml-1 text-xs font-normal text-slate-400'>
                                W
                            </span>
                        </p>
                    </div>
                    <div className='rounded-xl border border-white/10 bg-white/[0.03] p-3'>
                        <p className='flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-slate-500'>
                            <Thermometer className='size-3' aria-hidden='true' />
                            Temperature
                        </p>
                        <p className='mt-1 font-heading text-lg font-semibold' style={{ color: thermalColor(device.temperature_c) }}>
                            {device.temperature_c !== null ? device.temperature_c.toFixed(1) : '-'}
                            <span className='ml-1 text-xs font-normal text-slate-400'>
                                °C
                            </span>
                        </p>
                    </div>
                </div>
            </div>

            <div className='min-h-0 flex-1 overflow-y-auto px-5 py-3'>
                <dl>
                    {device.vendor && (
                        <Spec label='Vendor'>
                            <CmsText table='devices' id={device.id} field='vendor' value={device.vendor} />
                        </Spec>
                    )}
                    {device.model && (
                        <Spec label='Model'>
                            <CmsText table='devices' id={device.id} field='model' value={device.model} />
                        </Spec>
                    )}
                    {device.cpu && (
                        <Spec label='Compute'>
                            <CmsText table='devices' id={device.id} field='cpu' value={device.cpu} />
                        </Spec>
                    )}
                    {device.memory_gb !== null && (
                        <Spec label='Memory'>
                            <span>{formatInteger(device.memory_gb)}</span> <span className='text-slate-400'>GB</span>
                        </Spec>
                    )}
                    {device.storage_tb !== null && (
                        <Spec label='Storage'>
                            <span>{device.storage_tb.toLocaleString('en-US', { maximumFractionDigits: 2 })}</span> <span className='text-slate-400'>TB</span>
                        </Spec>
                    )}
                    {device.ports !== null && (
                        <Spec label='Ports'>
                            {device.ports}
                        </Spec>
                    )}
                    {device.ip_address && (
                        <Spec label='IP address'>
                            <CmsText table='devices' id={device.id} field='ip_address' value={device.ip_address} className='font-mono' />
                        </Spec>
                    )}
                    {device.os && (
                        <Spec label='Software'>
                            <CmsText table='devices' id={device.id} field='os' value={device.os} />
                        </Spec>
                    )}
                    {device.serial_number && (
                        <Spec label='Serial number'>
                            <CmsText table='devices' id={device.id} field='serial_number' value={device.serial_number} className='font-mono' />
                        </Spec>
                    )}
                    {device.asset_tag && (
                        <Spec label='Asset tag'>
                            <CmsText table='devices' id={device.id} field='asset_tag' value={device.asset_tag} className='font-mono' />
                        </Spec>
                    )}
                    <Spec label='Position'>
                        <span className='font-mono'>{unitRange(device)}</span>
                        <span className='text-slate-400'> · </span>
                        <span className='text-slate-400'>{device.u_height}</span>
                        <span className='text-slate-400'>U</span>
                    </Spec>
                    {installed && (
                        <Spec label='Installed'>
                            {installed}
                        </Spec>
                    )}
                    {warranty && (
                        <Spec label='Warranty until'>
                            <span className={expired ? 'text-amber-300' : undefined}>{warranty}</span>
                            {expired && <span className='ml-1.5 rounded bg-amber-400/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-300'>Expired</span>}
                        </Spec>
                    )}
                </dl>

                <div className='mt-4 grid grid-cols-2 gap-2 pb-2'>
                    <button
                        type='button'
                        onClick={copyHostname}
                        className='inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm font-medium text-slate-200 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70'
                    >
                        {copied ? <Check className='size-4 text-emerald-300' aria-hidden='true' /> : <Copy className='size-4' aria-hidden='true' />}
                        {copied ? 'Copied' : 'Copy hostname'}
                    </button>
                    <button
                        type='button'
                        onClick={findInInventory}
                        className='inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm font-medium text-slate-200 transition hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70'
                    >
                        <Search className='size-4' aria-hidden='true' />
                        In inventory
                    </button>
                </div>

                <p className='pb-1 text-center text-[11px] text-slate-600'>
                    {deviceVisual(device) === 'tape' ? 'The robotic accessor keeps moving while you look.' : 'Pulled out on its rails for inspection.'}
                </p>
            </div>
        </motion.div>
    )
}

const DetailPanel: FC = (): ReactNode => {
    const selectedRackId = useDatacenterStore(state => state.selectedRackId)
    const selectedDeviceId = useDatacenterStore(state => state.selectedDeviceId)
    const { rackById, deviceById, devicesByRack } = useInventory()
    const rack = selectedRackId ? rackById.get(selectedRackId) : undefined
    const device = selectedDeviceId ? deviceById.get(selectedDeviceId) : undefined
    const open = Boolean(rack || device)

    return (
        <AnimatePresence>
            {open && (
                <motion.aside
                    key='detail-panel'
                    aria-label={device ? 'Device details' : 'Rack details'}
                    className={cn(glass, 'pointer-events-auto absolute inset-x-2 bottom-2 z-30 flex max-h-[62vh] flex-col overflow-hidden md:inset-x-auto md:bottom-24 md:right-4 md:top-24 md:max-h-none md:w-[380px]')}
                    initial={{ opacity: 0, x: 60, scale: 0.98 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: 60, scale: 0.98 }}
                    transition={{ duration: 0.5, ease }}
                >
                    <div className='pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-300/60 to-transparent' />
                    <AnimatePresence mode='wait' initial={false}>
                        {device ? (
                            <DeviceDetail key={`device-${device.id}`} device={device} rack={rack} />
                        ) : rack ? (
                            <RackDetail key={`rack-${rack.id}`} rack={rack} devices={devicesByRack.get(rack.id) ?? []} />
                        ) : null}
                    </AnimatePresence>
                </motion.aside>
            )}
        </AnimatePresence>
    )
}

export default DetailPanel
